import React, { createContext, ReactNode, useCallback, useState } from 'react';
import BaseDialog from '../components/Dialogs/BaseDialog';
import { DialogAskForConfirmation } from '../components/Dialogs/DialogAskForConfirmation';
import { DialogAskForIncludeOrReplace } from '../components/Dialogs/DialogAskForIncludeOrReplace';

interface FeedbackContextProps {
  openBaseDialog(
    WrappedComponent: React.FC<DialogWrappedComponentProps>,
    options?: {
      persist?: boolean;
      width?: string;
      height?: string;
      marginTop?: string;
      hideCloseButton?: boolean;
      backgroundColor?: string;
      overflow?: string;
      onCloseCallback?(): void;
      onOverlayClickCallback?(): void;
    }
  ): Promise<void>;
  askForIncludeOrReplace(title: string, text: string): Promise<boolean | null>;
  askForConfirmation(props: {
    title: string;
    text: string;
    cancelText: string;
    confirmText: string;
  }): Promise<boolean | null>;
}

export const FeedbackContext = createContext<FeedbackContextProps>({
  openBaseDialog: async (
    WrappedComponent: React.FC<any>,
    options?: {
      persist?: boolean;
      width?: string;
      height?: string;
      marginTop?: string;
      hideCloseButton?: boolean;
      backgroundColor?: string;
      overflow?: string;
      onCloseCallback?(): void;
    }
  ) => {},
  askForIncludeOrReplace: async (title: string, text: string) => null,
  askForConfirmation: async (props: { title: string; text: string; cancelText: string; confirmText: string }) => null,
});

export interface FeedbackProviderInterface {
  children: ReactNode;
}

export function FeedbackProvider(props: FeedbackProviderInterface) {
  const [dialogs, setDialogs] = useState<Array<React.FC>>([]);

  /**
   * Não utilize nenhum outro contexto dentro do componente a ser renderizado nesta função, o componente não terá acesso.
   * @param WrappedComponent
   * @param options
   */
  const openBaseDialog = useCallback(
    (
      WrappedComponent: React.FC<any>,
      options?: {
        persist?: boolean;
        width?: string;
        height?: string;
        marginTop?: string;
        backgroundColor?: string;
        boxShadow?: string;
        overflow?: string;
        onCloseCallback?(): void;
        onOverlayClickCallback?(): void;
      }
    ): Promise<void> => {
      return new Promise((resolve) => {
        const Component = () => {
          const [isOpen, setIsOpen] = useState(true);

          const onClose = () => {
            setIsOpen(false);
            resolve();

            if (options?.onCloseCallback) {
              options.onCloseCallback();
            }
          };

          const onOverlayClick = () => {
            setIsOpen(false);
            resolve();

            if (options?.onOverlayClickCallback) {
              options.onOverlayClickCallback();
            }
          };

          const stylesBase = {
            height: options?.height || '414px',
            marginTop: options?.marginTop || 'none',
            overflow: options?.overflow || 'hidden',
            padding: '24px',
            backgroundColor: options?.backgroundColor || '#fff',
            boxShadow: options?.backgroundColor ? 'none' : '0px 0px 15px 5px rgba(0,0,0,0.22)',
          };

          return (
            <BaseDialog
              isOpen={isOpen}
              persist={options?.persist}
              onClose={onClose}
              width={options?.width || '414px'}
              stylesCustom={stylesBase}
              onOverlayClick={onOverlayClick}
            >
              <WrappedComponent onClose={onClose} />
            </BaseDialog>
          );
        };

        setDialogs((prevState) => [...prevState, Component]);
      });
    },
    []
  );

  const askForIncludeOrReplace = useCallback((title: string, text: string): Promise<boolean | null> => {
    return new Promise((resolve) => {
      const Component = () => {
        const [isOpen, setIsOpen] = useState(true);

        const onClose = () => {
          setIsOpen(false);
          resolve(null);
        };

        const onConfirm = (replaceItems: boolean) => {
          setIsOpen(false);
          resolve(replaceItems);
        };

        return (
          <DialogAskForIncludeOrReplace
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            text={text}
            onConfirm={onConfirm}
          />
        );
      };

      setDialogs((prevState) => [...prevState, Component]);
    });
  }, []);

  const askForConfirmation = useCallback(
    (props: { title: string; text: string; cancelText: string; confirmText: string }): Promise<boolean | null> => {
      return new Promise((resolve) => {
        const Component = () => {
          const [isOpen, setIsOpen] = useState(true);

          const onClose = () => {
            setIsOpen(false);
            resolve(null);
          };

          const onConfirm = () => {
            setIsOpen(false);
            resolve(true);
          };

          const onCancel = () => {
            setIsOpen(false);
            resolve(false);
          };

          return (
            <DialogAskForConfirmation
              isOpen={isOpen}
              title={props.title}
              text={props.text}
              cancelText={props.cancelText}
              confirmText={props.confirmText}
              onConfirm={onConfirm}
              onClose={onClose}
              onCancel={onCancel}
            />
          );
        };

        setDialogs((prevState) => [...prevState, Component]);
      });
    },
    []
  );

  return (
    <FeedbackContext.Provider
      value={{
        openBaseDialog,
        askForIncludeOrReplace,
        askForConfirmation,
      }}
    >
      <>
        {props.children}
        {dialogs.map((Component, index) => (
          <Component key={index} />
        ))}
      </>
    </FeedbackContext.Provider>
  );
}

export interface DialogWrappedComponentProps {
  onClose(): void;
}
